import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Cloud, Droplets, Wind, Sun, CloudRain, CloudFog, Thermometer, MapPin } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WeatherWidget = ({ className = '', usePublic = false }) => {
  const { t } = useLanguage();
  const { token } = useAuth();
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        const endpoint = usePublic || !token ? `${API}/weather/public` : `${API}/weather`;
        const config = token && !usePublic ? { headers: { Authorization: `Bearer ${token}` } } : {};
        const response = await axios.get(endpoint, config);
        setWeather(response.data);
      } catch (error) {
        console.error('Failed to fetch weather:', error);
        // Fallback mock data
        setWeather({
          location: '南山村, 大同鄉, 宜蘭縣',
          location_en: 'Nanshan Village, Datong Township, Yilan County',
          temperature: 22,
          humidity: 78,
          description: '多雲 Cloudy',
          icon: '02d',
          wind_speed: 2.5,
          is_mock: true
        });
      } finally {
        setLoading(false);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 300000); // Update every 5 minutes
    return () => clearInterval(interval);
  }, [token, usePublic]);

  const getWeatherIcon = () => {
    if (!weather) return <Cloud className="w-10 h-10" />;
    const desc = weather.description.toLowerCase();
    if (desc.includes('rain') || desc.includes('雨')) return <CloudRain className="w-10 h-10 text-fac-blue" />;
    if (desc.includes('mist') || desc.includes('fog') || desc.includes('霧')) return <CloudFog className="w-10 h-10 text-gray-400" />;
    if (desc.includes('cloud') || desc.includes('雲') || desc.includes('陰')) return <Cloud className="w-10 h-10 text-gray-400" />;
    return <Sun className="w-10 h-10 text-fac-yellow" />;
  };

  if (loading) {
    return (
      <div className={`bg-fac-card border border-border rounded-lg p-4 ${className}`}>
        <div className="animate-pulse flex items-center gap-4">
          <div className="w-10 h-10 bg-muted rounded-full" />
          <div className="flex-1 space-y-2">
            <div className="h-4 bg-muted rounded w-32" />
            <div className="h-6 bg-muted rounded w-20" />
          </div>
        </div>
      </div>
    );
  }

  if (!weather) return null;

  return (
    <div 
      className={`bg-fac-card border border-border rounded-lg p-5 ${className}`}
      data-testid="weather-widget"
    >
      {/* Location Header */}
      <div className="flex items-center gap-2 mb-3 text-muted-foreground">
        <MapPin className="w-4 h-4 text-fac-green" />
        <span className="text-sm font-chinese">{weather.location}</span>
      </div>
      
      <div className="flex items-center gap-4">
        {getWeatherIcon()}
        <div className="flex-1">
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-white">{weather.temperature}°C</p>
            {weather.feels_like && (
              <p className="text-sm text-muted-foreground">
                Feels {weather.feels_like}°C
              </p>
            )}
          </div>
          <p className="text-sm text-fac-green font-chinese">{weather.description}</p>
        </div>
        
        <div className="flex flex-col gap-2 text-sm">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Droplets className="w-4 h-4 text-fac-blue" />
            <span>{weather.humidity}%</span>
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <Wind className="w-4 h-4" />
            <span>{weather.wind_speed} m/s</span>
          </div>
        </div>
      </div>

      {/* Coordinates and elevation info */}
      {weather.coordinates && (
        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
          <span className="font-chinese">
            {weather.coordinates.lat}°N, {weather.coordinates.lon}°E
          </span>
          {weather.elevation_note && (
            <span>{weather.elevation_note}</span>
          )}
        </div>
      )}

      {weather.is_mock && (
        <p className="text-xs text-fac-yellow mt-2 font-chinese">
          Demo data • Add API key in Settings / 示範數據 • 請在設定中新增 API 金鑰
        </p>
      )}
    </div>
  );
};

export default WeatherWidget;
